import { queryOptions } from '@tanstack/react-query'
import type { Choice, Written } from './businessApps'
import { apiGet, apiPost, type PostValue } from './client'

/**
 * Aplicatiile de business gasite singure - `v4/api/src/Controller/BusinessAppsDetectController.php`.
 *
 *   GET  /business-apps/suggestions               sugestiile firmei curente
 *   POST /business-apps/suggestions/run           "Find applications" (firma din bara; company_id nu o muta)
 *   POST /business-apps/suggestions/{id}/confirm  components[i][device_id|role], add_workstations, name
 *   POST /business-apps/suggestions/{id}/dismiss
 *   GET  /business-apps/catalog                   catalogul platformei + al furnizorului
 *   POST /business-apps/catalog/save              intrare proprie
 *   POST /business-apps/catalog/active            code, active
 */

export type SuggestedComponent = {
  key: string
  deviceId: number
  deviceName: string
  /** Masina e in CMDB: componenta primeste muchia `depends_on`. */
  inCmdb: boolean
  role: number
  roleLabel: string
  evidence: string[]
}

export type Suggestion = {
  id: number
  code: string
  name: string
  vendor: string
  description: string
  companyId: number
  companyName: string
  /** 3 high, 2 medium, 1 low */
  confidence: number
  confidenceLabel: string
  existingAppId: number | null
  existingAppName: string | null
  components: SuggestedComponent[]
  workstations: { count: number; names: string[] }
  detectedAt: string | null
}

export type SuggestionsPage = {
  ready: boolean
  message?: string
  canEdit: boolean
  companyId: number
  /** Nicio firma aleasa in bara: nu se cauta si nu se arata nimic. */
  needsCompany?: boolean
  lastRun: string | null
  suggestions: Suggestion[]
  companies: { id: number; name: string }[]
  roles: Choice[]
}

export function suggestionsQuery() {
  return queryOptions({
    queryKey: ['business-apps', 'suggestions'],
    queryFn: () => apiGet<SuggestionsPage>('/business-apps/suggestions'),
  })
}

export type RunResult = Written & { found?: number }

export function findApplications(companyId: number) {
  return apiPost<RunResult>('/business-apps/suggestions/run', { company_id: companyId })
}

export type ConfirmResult = Written & { created?: boolean; added?: number }

export function confirmSuggestion(id: number, fields: { components: { device_id: number; role: number }[]; add_workstations: boolean; name: string }) {
  return apiPost<ConfirmResult>(`/business-apps/suggestions/${id}/confirm`, {
    components: fields.components,
    add_workstations: fields.add_workstations ? 1 : 0,
    name: fields.name,
  })
}

export function dismissSuggestion(id: number) {
  return apiPost<Written>(`/business-apps/suggestions/${id}/dismiss`, {})
}

/* -------------------------------------------------------------------------
 * catalogul
 * ---------------------------------------------------------------------- */

export type CatalogRule = { kind: string; pattern: string; role: number; weight: number; colocated: boolean }

export type CatalogEntry = {
  code: string
  name: string
  vendor: string
  description: string
  criticality: number
  match: string[]
  rules: CatalogRule[]
  contact: { name: string; email: string; phone: string; url: string } | null
  /** Intrarea furnizorului (nu a platformei). */
  own: boolean
  /** Intrarea furnizorului inlocuieste una a platformei. */
  overridden: boolean
  active: boolean
}

export type CatalogPage = {
  ready: boolean
  canEdit: boolean
  entries: CatalogEntry[]
  kinds: { value: string; label: string }[]
  roles: Choice[]
  criticality: Choice[]
}

export function catalogQuery() {
  return queryOptions({
    queryKey: ['business-apps', 'catalog'],
    queryFn: () => apiGet<CatalogPage>('/business-apps/catalog'),
  })
}

export type CatalogFields = {
  code: string
  name: string
  vendor: string
  description: string
  criticality: number
  /** Un nume pe rand. */
  match: string
  rules: CatalogRule[]
  contact_name: string
  contact_email: string
  contact_phone: string
  contact_url: string
}

export function saveCatalogEntry(fields: CatalogFields) {
  return apiPost<Written & { code?: string }>('/business-apps/catalog/save', {
    ...fields,
    rules: fields.rules.map((r) => ({ kind: r.kind, pattern: r.pattern, role: r.role, weight: r.weight, colocated: r.colocated ? 1 : 0 })),
  } as unknown as Record<string, PostValue>)
}

export function setCatalogActive(code: string, active: boolean) {
  return apiPost<Written>('/business-apps/catalog/active', { code, active: active ? 1 : 0 })
}
